import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useInView } from "react-intersection-observer";

export default function WhyChoose() {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const scale = useTransform(scrollYProgress, [0, 0.5], [0.8, 1]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <section
      className="relative bg-[#F2FCDA] WhyChoose-section py-20 lg:py-40"
      ref={sectionRef}
      id="feature-section"
    >
      <div className="container mx-auto px-4" ref={ref}>
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.h1 
            id="features" 
            className="text-[28px] md:text-[42px] lg:text-[56px] font-bold mb-4"
            style={{ scale }}
          >
            Why Choose Nudgie?
          </motion.h1>
        </motion.div>
        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center mt-8"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {data.map((item, index) => (
            <motion.div
              key={item.id}
              className="relative bg-white rounded-xl px-8 md:px-10 py-8 md:py-20 bg-[#f7fbf4] border border-[#2E2E1F1F] h-full hover:shadow-lg transition-shadow duration-300"
              variants={itemVariants}
              whileHover={{ y: -5 }}
            >
              <div className="h-[36px] w-[36px] rounded-full border border-[#2E2E1F1F] flex items-center justify-center mb-4">
                {item.icon}
              </div>
              <h4 className="text-[18px] md:text-[28px] font-bold mb-4">
                {item.name}
              </h4>
              <p className="text-[14px] md:text-[18px]">{item.detail}</p>
            </motion.div>
          ))}
        </motion.div>
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 gap-4 items-center mt-4"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {data1.map((item, index) => (
            <motion.div
              key={item.id}
              className="relative bg-white rounded-xl px-8 md:px-10 py-8 bg-[#f7fbf4] md:py-20 border border-[#2E2E1F1F] h-full hover:shadow-lg transition-shadow duration-300"
              variants={itemVariants}
              whileHover={{ y: -5 }}
            >
              <div className="h-[36px] w-[36px] rounded-full border border-[#2E2E1F1F] flex items-center justify-center mb-4">
                {item.icon}
              </div>
              <h4 className="text-[18px] md:text-[28px] font-bold mb-4">
                {item.name}
              </h4>
              <p className="text-[14px] md:text-[18px]">{item.detail}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

const data = [
  {
    id: 1,
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8.99996 0.666626C13.6025 0.666626 17.3333 4.39746 17.3333 8.99996C17.3333 13.6025 13.6025 17.3333 8.99996 17.3333C4.39746 17.3333 0.666626 13.6025 0.666626 8.99996C0.666626 4.39746 4.39746 0.666626 8.99996 0.666626ZM8.99996 2.33329C7.23185 2.33329 5.53616 3.03567 4.28591 4.28591C3.03567 5.53616 2.33329 7.23185 2.33329 8.99996C2.33329 10.7681 3.03567 12.4638 4.28591 13.714C5.53616 14.9642 7.23185 15.6666 8.99996 15.6666C10.7681 15.6666 12.4638 14.9642 13.714 13.714C14.9642 12.4638 15.6666 10.7681 15.6666 8.99996C15.6666 7.23185 14.9642 5.53616 13.714 4.28591C12.4638 3.03567 10.7681 2.33329 8.99996 2.33329ZM8.99996 3.99996C9.20407 3.99999 9.40107 4.07492 9.5536 4.21056C9.70613 4.34619 9.80358 4.53308 9.82746 4.73579L9.83329 4.83329V8.65496L12.0891 10.9108C12.2386 11.0608 12.3254 11.262 12.3318 11.4736C12.3383 11.6852 12.2639 11.8914 12.1239 12.0502C11.9839 12.209 11.7887 12.3085 11.5779 12.3286C11.3671 12.3487 11.1566 12.2878 10.9891 12.1583L10.9108 12.0891L8.41079 9.58913C8.28128 9.4595 8.19809 9.29079 8.17413 9.10913L8.16663 8.99996V4.83329C8.16663 4.61228 8.25442 4.40032 8.4107 4.24404C8.56698 4.08776 8.77894 3.99996 8.99996 3.99996Z"
          fill="black"
        />
      </svg>
    ),
    name: "Hyper-Personalization",
    detail:
      "Your plan will be tailored to your personality, work style, schedule, and goals.",
  },
  {
    id: 2,
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M21 11V9H19V7C18.9984 6.47005 18.7872 5.96227 18.4125 5.58753C18.0377 5.2128 17.5299 5.00158 17 5H15V3H13V5H11V3H9V5H7C6.47005 5.00158 5.96227 5.2128 5.58753 5.58753C5.2128 5.96227 5.00158 6.47005 5 7V9H3V11H5V13H3V15H5V17C5.00158 17.5299 5.2128 18.0377 5.58753 18.4125C5.96227 18.7872 6.47005 18.9984 7 19H9V21H11V19H13V21H15V19H17C17.5299 18.9984 18.0377 18.7872 18.4125 18.4125C18.7872 18.0377 18.9984 17.5299 19 17V15H21V13H19V11H21ZM17 17H7V7H17V17Z"
          fill="black"
        />
        <path
          d="M11.3611 8H10.0161L8.0061 16H9.0331L9.4971 14.125H11.8131L12.2651 16H13.3271L11.3611 8ZM9.6321 13.324L10.6501 8.95H10.6961L11.6791 13.324H9.6321ZM14.2441 8H15.2441V16H14.2441V8Z"
          fill="black"
        />
      </svg>
    ),
    name: "True Accountability",
    detail:
      "We actually hold you to your commitments and make it difficult to slack off.",
  },
];
const data1 = [
  {
    id: 1,
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M1.5 14H15V13H2V0H1V13.5L1.5 14ZM3 11.5V3.5L3.5 3H5.5L6 3.5V11.5L5.5 12H3.5L3 11.5ZM5 11V4H4V11H5ZM11 1.5V11.5L11.5 12H13.5L14 11.5V1.5L13.5 1H11.5L11 1.5ZM13 2V11H12V2H13ZM7 11.5V5.5L7.5 5H9.5L10 5.5V11.5L9.5 12H7.5L7 11.5ZM9 11V6H8V11H9Z"
          fill="black"
        />
      </svg>
    ),
    name: "Progress Tracking and Metrics",
    detail:
      "Nudgie automatically tracks your progress and generates intuitive graphs.",
  },
  {
    id: 2,
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0 16H18V18H0V16ZM3 13C4.1 13 5 12.1 5 11C5 10.5 4.8 10 4.5 9.7L5.8 7H6C6.5 7 7 6.8 7.3 6.5L10 7.9V8C10 9.1 10.9 10 12 10C13.1 10 14 9.1 14 8C14 7.5 13.8 7.1 13.5 6.7L14.8 4H15C16.1 4 17 3.1 17 2C17 0.9 16.1 0 15 0C13.9 0 13 0.9 13 2C13 2.5 13.2 3 13.5 3.3L12.2 6H12C11.5 6 11 6.2 10.7 6.5L8 5.2V5C8 3.9 7.1 3 6 3C4.9 3 4 3.9 4 5C4 5.5 4.2 6 4.5 6.3L3.2 9H3C1.9 9 1 9.9 1 11C1 12.1 1.9 13 3 13Z"
          fill="black"
        />
      </svg>
    ),
    name: "Constant Wins",
    detail:
      "A milestone-based approach. We believe small wins lead to big wins, and we'll design your plan accordingly.",
  },
  {
    id: 3,
    icon: (
      <svg
        width="21"
        height="22"
        viewBox="0 0 21 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8.5 3C8.5 3.79565 8.18393 4.55871 7.62132 5.12132C7.05871 5.68393 6.29565 6 5.5 6C4.70435 6 3.94129 5.68393 3.37868 5.12132C2.81607 4.55871 2.5 3.79565 2.5 3C2.5 2.20435 2.81607 1.44129 3.37868 0.87868C3.94129 0.31607 4.70435 0 5.5 0C6.29565 0 7.05871 0.31607 7.62132 0.87868C8.18393 1.44129 8.5 2.20435 8.5 3ZM1.768 7C0.79 7 0 7.791 0 8.768V9.128C0 9.335 5.96046e-08 10.536 0.7 11.703C1.248 12.613 2.177 13.43 3.675 13.796C3.87318 13.2684 4.22758 12.8138 4.6909 12.4929C5.15421 12.172 5.7044 12 6.268 12H7.354C6.92842 11.6246 6.58758 11.163 6.35414 10.6457C6.12069 10.1285 5.99997 9.56748 6 9C6 8.271 6.195 7.588 6.535 7H1.768ZM13.465 7C13.8158 7.60816 14.0003 8.29793 14 9C14 9.426 13.934 9.837 13.81 10.222C14.3611 10.0741 14.9294 9.9995 15.5 10C17.001 10 18.383 10.509 19.484 11.364C20 10.313 20 9.315 20 9.127V8.767C20 7.792 19.209 7 18.232 7H13.465ZM10.022 13C9.35284 14.0446 8.99809 15.2595 9 16.5C8.99809 17.7405 9.35284 18.9554 10.022 20H10C7.386 20 5.942 18.937 5.2 17.703C4.5 16.536 4.5 15.336 4.5 15.127V14.767C4.5 13.791 5.291 13 6.268 13H10.022ZM14.5 6C14.894 6 15.2841 5.9224 15.6481 5.77164C16.012 5.62087 16.3427 5.3999 16.6213 5.12132C16.8999 4.84274 17.1209 4.51203 17.2716 4.14805C17.4224 3.78407 17.5 3.39397 17.5 3C17.5 2.60603 17.4224 2.21593 17.2716 1.85195C17.1209 1.48797 16.8999 1.15726 16.6213 0.87868C16.3427 0.600104 16.012 0.379125 15.6481 0.228361C15.2841 0.0775973 14.894 -5.87054e-09 14.5 0C13.7044 1.18561e-08 12.9413 0.31607 12.3787 0.87868C11.8161 1.44129 11.5 2.20435 11.5 3C11.5 3.79565 11.8161 4.55871 12.3787 5.12132C12.9413 5.68393 13.7044 6 14.5 6ZM15.5 11C16.9587 11 18.3576 11.5795 19.3891 12.6109C20.4205 13.6424 21 15.0413 21 16.5C21 17.9587 20.4205 19.3576 19.3891 20.3891C18.3576 21.4205 16.9587 22 15.5 22C14.0413 22 12.6424 21.4205 11.6109 20.3891C10.5795 19.3576 10 17.9587 10 16.5C10 15.0413 10.5795 13.6424 11.6109 12.6109C12.6424 11.5795 14.0413 11 15.5 11ZM16.001 19.503V17H18.497C18.6296 17 18.7568 16.9473 18.8506 16.8536C18.9443 16.7598 18.997 16.6326 18.997 16.5C18.997 16.3674 18.9443 16.2402 18.8506 16.1464C18.7568 16.0527 18.6296 16 18.497 16H16V13.5C16 13.3674 15.9473 13.2402 15.8536 13.1464C15.7598 13.0527 15.6326 13 15.5 13C15.3674 13 15.2402 13.0527 15.1464 13.1464C15.0527 13.2402 15 13.3674 15 13.5V16H12.496C12.3634 16 12.2362 16.0527 12.1424 16.1464C12.0487 16.2402 11.996 16.3674 11.996 16.5C11.996 16.6326 12.0487 16.7598 12.1424 16.8536C12.2362 16.9473 12.3634 17 12.496 17H15V19.503C15 19.6356 15.0527 19.7628 15.1464 19.8566C15.2402 19.9503 15.3674 20.003 15.5 20.003C15.6326 20.003 15.7598 19.9503 15.8536 19.8566C15.9473 19.7628 16.001 19.6356 16.001 19.503ZM7.004 9.154C7.03409 9.7392 7.23491 10.3028 7.58165 10.7752C7.92839 11.2475 8.40588 11.608 8.95517 11.8121C9.50445 12.0162 10.1015 12.0549 10.6725 11.9236C11.2436 11.7922 11.7637 11.4965 12.1686 11.0729C12.5735 10.6494 12.8455 10.1165 12.951 9.54008C13.0565 8.96368 12.9909 8.369 12.7623 7.82947C12.5337 7.28993 12.152 6.82916 11.6645 6.50405C11.177 6.17893 10.605 6.0037 10.019 6H10C9.20435 6 8.44129 6.31607 7.87868 6.87868C7.31607 7.44129 7 8.20435 7 9L7.004 9.154Z"
          fill="black"
        />
      </svg>
    ),
    name: "AI-Enhanced Habits",
    detail:
      "Whenever we're able, we'll generate AI-based exercises to supercharge your progress.",
  },
];
